import { InfiniteScrollSearchManager } from "@search/InfiniteScrollSearchManager";
import { TicketsSearchFilterManager } from "@tickets/TicketSearch/TicketsSearchFilterManager";
import {
  SortOrder,
  TodoSearch as Fetch,
  TodoSearchItemFragment,
  TodoSearchSortBy,
  GetTodoTotal
} from "@graphql";
import { store } from "@store";
import { List } from "react-virtualized";
import { action, autorun, observable } from "mobx";
import { SortOption } from "@search/SortOptions";
import { ObservableQuery } from "@apollo/client";
import { TicketSearchFormData } from "@tickets/TicketSearch/TicketSearchFormData";

export type TicketItemEditorDataType = {
  text: string;
  dueDate: Date | null;
};

type TicketsSearchStoreOptions = {
  initialFilter: TicketSearchFormData;
  isShowTodoTotal?: boolean;
};

export class TicketsSearchStore {
  query: ObservableQuery<Fetch.Return, Fetch.Variables>;
  filterManager: TicketsSearchFilterManager;
  searchManager: InfiniteScrollSearchManager<TodoSearchItemFragment>;
  @observable _sortOption = {
    by: TodoSearchSortBy.Create,
    dir: -1
  };
  listRef: List = null;
  @observable hasMore = true;
  @observable isShowTodoTotal = false;
  @observable todoTotal = 0;

  constructor(opts?: TicketsSearchStoreOptions) {
    this.fetch = this.fetch.bind(this);
    this.filterManager = new TicketsSearchFilterManager(opts.initialFilter, this.fetch);
    this.searchManager = new InfiniteScrollSearchManager(this.fetch, true);

    if (opts.isShowTodoTotal !== undefined) {
      this.isShowTodoTotal = opts.isShowTodoTotal;
    }

    store().todoStore.setTodoSearchStore(this);

    autorun(() => {
      if (
        store().todoStore.selectedTeam &&
        !this.filterManager.searchFilters.teamIds.length
      ) {
        this.filterManager.updateFilter({
          teamIds: [store().todoStore.selectedTeam?.NodeID].filter((x) => x)
        });
      }
    });
  }

  init() {
    this.query = Fetch.funcWatch(
      {
        filter: this.filterManager.searchFilters,
        limit: 20,
        skip: 0,
        sortBy: this.sortOption.by,
        sortOrder: this.sortOption.dir === 1 ? SortOrder.Asc : SortOrder.Desc
      },
      { fetchPolicy: "network-only" }
    );

    if (this.isShowTodoTotal) {
      this.fetchTotal();
    } else {
      this.todoTotal = 0;
    }

    this.subscribe();
  }

  subscribe() {
    this.query.subscribe({
      next: ({ data }) => {
        if (!data.todoSearch.length) {
          this.hasMore = false;
        }

        this.searchManager.merge(data.todoSearch);
      },
      error: () => {
        this.hasMore = false;
        this.searchManager.merge([]);
      }
    });
  }

  @action showTodoTotal = () => {
    this.isShowTodoTotal = true;
    this.fetchTotal();
  };

  fetchTotal() {
    GetTodoTotal.func(
      {
        filter: this.filterManager.searchFilters
      },
      { fetchPolicy: "network-only" }
    ).then(({ data }) => {
      this.todoTotal = data.todoTotal.total;
    });
  }

  fetch(skip?: number, limit?: number) {
    const _skip = skip == null ? 0 : skip;
    const _limit = limit == null ? 20 : limit;

    const isNew = !skip;
    isNew && this.searchManager?.clear();

    if (isNew) {
      this.hasMore = true;
      this.init();
      return Promise.resolve();
    } else {
      return this.query.fetchMore({
        variables: {
          filter: this.filterManager.searchFilters,
          limit: _limit,
          skip: _skip,
          sortBy: this.sortOption.by,
          sortOrder: this.sortOption.dir === 1 ? SortOrder.Asc : SortOrder.Desc
        },
        updateQuery: (previousQueryResult, options) => {
          if (!options.fetchMoreResult.todoSearch.length) {
            this.hasMore = false;
          }

          return {
            ...previousQueryResult,
            todoSearch: [
              ...previousQueryResult.todoSearch,
              ...options.fetchMoreResult.todoSearch
            ]
          };
        }
      });
    }
  }

  get sortOption() {
    return this._sortOption;
  }

  set sortOption(option) {
    this._sortOption = option;
  }

  @action.bound sortTickets(option: SortOption) {
    const [by, dir] = option.value.split("_");
    this.sortOption = {
      by: by as TodoSearchSortBy,
      dir: dir ? Number(dir) : -1
    };

    this.fetch();
  }
}
